/**
 * ATL-888 — make the read-cutover switch take effect on a RUNNING app.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE PROBLEM
 *
 * ATL-886 made the per-org read cutover work; it is resolved in `AppStack.getInitialData`, at
 * startup. But `shouldUseSupabase()` is **not** a session-wide decision: it is re-evaluated at ~55
 * call sites, per render and per refetch. Only the SUBSCRIPTIONS and the initial atom load are
 * pinned to what startup decided.
 *
 * So changing the flag on a running app does not do nothing, and it does not do the right thing
 * either — it produces a **split-brain till**: newly rendered screens read one store while the
 * atoms and realtime subscriptions are still fed from the other. In the OFF direction that till is
 * also, for as long as it runs, ignoring an operator who has declared the store unsafe. A rollback
 * only truly landed when the till was next launched — hours later, one at a time, in an order
 * nobody controls. **We were about to cut organisations over with a kill switch we could not
 * execute.**
 *
 * This module watches the flag for the life of the session and RELOADS the app when the resolved
 * data source changes, which makes the switch atomic per device in both directions.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHY BOTH DIRECTIONS RELOAD, THOUGH ONLY ONE IS URGENT
 *
 * The original brief was "reload on OFF; ON can wait for the next launch". That rested on the
 * premise that turning reads ON mid-session is inert until restart. It is not — see above; it
 * half-cuts the till over immediately. A half-cut-over till is a worse state than either endpoint,
 * so the correct answer for ON is also a reload.
 *
 * The asymmetry that survives is URGENCY, not mechanism, and it lives in the messaging: OFF is an
 * incident response, ON is a planned cutover. The rule itself is direction-agnostic — reload
 * whenever the store a fresh start would choose differs from the one this session is serving —
 * which is also why it cannot loop: after a reload those two agree by construction.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHY THIS WATCHES **FIRESTORE**, EVEN WHEN THE SESSION IS READING SUPABASE
 *
 * The flag is dual-written: `organizations.useSupabaseForData` in Firestore and
 * `organizations.use_supabase_for_data` in Supabase. The CLIENT gates on the Firestore copy
 * (`primeOrganizationForReadCutover`, ATL-886). A device reading Supabase has no Firestore
 * organization listener — that one lives inside `getInitialData`'s `if (!useSupabase)` branch — so
 * Supabase realtime was the path of least resistance. It is the wrong one:
 *
 *   1. **A kill switch must not depend on the system it kills.** The likeliest reason to roll an
 *      org back is that Supabase is misbehaving for it. A rollback signal delivered over Supabase
 *      realtime is unavailable in exactly the incident it exists for.
 *   2. **It is the copy the consumer gates on.** Watching the other one would mean reacting to a
 *      value that does not decide anything — which is precisely how both "read cutovers" in the
 *      record turned out to be no-ops.
 *   3. Firebase Auth is unchanged until Phase 7, so the Firestore SDK is live in both read modes.
 *
 * ⚠️ In Supabase read mode `organizationAtom` is refilled from the SUPABASE copy, so the per-call
 * `shouldUseSupabase()` sites resolve the flag from Supabase while this watch resolves it from
 * Firestore. If the two halves ever diverge the session and the kill switch disagree, so a
 * divergence is detected and reported (`onFlagDivergence`) rather than left silent.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHAT IT REFUSES TO DO
 *
 * **An unreadable flag never reloads.** A listener blip reloading every till at once is a worse
 * incident than the one being prevented, and the safe direction is where the next cold start goes
 * anyway. It is logged loudly instead — "the kill switch is blind" must not be learned from silence.
 *
 * **A cached snapshot never reloads.** Firestore serves the local cache first and the server copy
 * after. Acting on the cached emission can restart a till on a value that is already stale.
 * Cache-only emissions are treated as no evidence at all.
 *
 * **It does not act before the session has chosen a data source** — but it does not throw that
 * observation away either. The last one is retained and re-decided the moment the session mode is
 * recorded, because the flip can land inside the startup window.
 */

import { snapshotExists } from './types'
import type { OrganizationDocSnapshot } from './orgReadCutover'
import type { WritesMasterObservation } from '../Write/writeMode'

/** What a single look at the organization document told us. */
export type ReadCutoverObservation =
  /** The document was read from the SERVER. `cutover` is the flag it carries. */
  | { status: 'flag'; cutover: boolean }
  /** A cache-only emission. Real data, but not fresh enough to restart a till on. */
  | { status: 'cached' }
  /** The document is missing, or carries no payload. */
  | { status: 'absent' }
  /** The listener errored. */
  | { status: 'error'; error?: unknown }

export type LiveReloadAction =
  /** Reads must return to Firestore and this session serves Supabase — reload (incident). */
  | 'rollback'
  /** Reads must move to Supabase and this session serves Firestore — reload (planned cutover). */
  | 'cutover'
  /** The flag says Supabase is not wanted, but this device is pinned there by its own override. */
  | 'pinned'
  /** The observation agrees with what this session is already serving. */
  | 'unchanged'
  /** The flag could not be read, or was not fresh. Never reloads — but never silent either. */
  | 'unresolved'
  /** The session has not chosen a data source yet; the observation is retained, not discarded. */
  | 'deferred'

export interface LiveReloadDecision {
  action: LiveReloadAction
  reload: boolean
}

export interface DecideReadCutoverReloadParams {
  /**
   * What this session is ACTUALLY serving — recorded at the branch in `getInitialData`, not
   * re-derived from the flag. Reading the consumer rather than the store is the lesson of ATL-886
   * and it is the whole reason this comparison can detect anything.
   *
   * `null` means the branch has not run yet.
   */
  sessionReadsSupabase: boolean | null
  /** `getDeviceSupabaseOverride()` — the per-device QA toggle. */
  deviceOverride: boolean
  /** `localDevSupabaseReads()` — dead in production builds. */
  localDev: boolean
  observation: ReadCutoverObservation
}

/**
 * Decide what a single observation means for a running session.
 *
 * Pure, so the rule can be tested exhaustively without a Firestore, a timer or a React tree.
 */
export const decideReadCutoverReload = ({
  sessionReadsSupabase,
  deviceOverride,
  localDev,
  observation,
}: DecideReadCutoverReloadParams): LiveReloadDecision => {
  // Not an instruction: see "WHAT IT REFUSES TO DO".
  if (observation.status === 'error' || observation.status === 'absent') {
    return { action: 'unresolved', reload: false }
  }
  if (observation.status === 'cached') {
    return { action: 'unresolved', reload: false }
  }

  // The branch has not run. The caller retains this observation and re-decides when it has.
  if (sessionReadsSupabase === null) {
    return { action: 'deferred', reload: false }
  }

  // What a cold start would choose right now — the same disjunction as `shouldUseSupabase()`, so
  // this cannot drift from the thing it mirrors.
  //
  // NOTE: the `deviceOverride` term is also what makes the `pinned` case below resolve to "no
  // change". It is deliberately spelled out in full rather than relying on that branch, so the
  // mirror stays readable as a mirror.
  const nextReadsSupabase = localDev || deviceOverride || observation.cutover

  if (nextReadsSupabase === sessionReadsSupabase) {
    // Distinguish "nothing changed" from "the org flag was cleared but this device stays on
    // Supabase anyway" — otherwise an operator who pulls the switch on a QA device is left staring
    // at a kill switch that visibly did nothing.
    if (sessionReadsSupabase && !observation.cutover && (localDev || deviceOverride)) {
      return { action: 'pinned', reload: false }
    }
    return { action: 'unchanged', reload: false }
  }

  // The resolved data source has changed. Reload, so the switch is atomic on this device rather
  // than leaving the atoms and subscriptions serving one store while fresh reads use the other.
  return { action: nextReadsSupabase ? 'cutover' : 'rollback', reload: true }
}

/**
 * Read an organization snapshot into an observation. Never throws.
 *
 * `fromCache` is honoured: Firestore emits the local copy first and the server copy after, and a
 * till must not restart on the former.
 */
export const observeOrganizationSnapshot = (
  snapshot: OrganizationDocSnapshot | null | undefined
): ReadCutoverObservation => {
  try {
    // `snapshotExists` handles both SDK shapes — boolean property on web (firebase/compat), method
    // on native v22. Getting that wrong fails silently and once broke every Firestore read on web
    // (ATL-570), so it is never re-implemented inline.
    if (!snapshot || !snapshotExists(snapshot)) {
      return { status: 'absent' }
    }

    const metadata = (snapshot as { metadata?: { fromCache?: unknown } }).metadata
    if (metadata && metadata.fromCache === true) {
      return { status: 'cached' }
    }

    const data = snapshot.data?.()
    if (!data) {
      return { status: 'absent' }
    }
    // Strict `=== true`, the same comparison `getOrgSupabaseReadCutover()` makes. Anything else —
    // `undefined`, `false`, a string — means "not cut over", which is the safe direction.
    return { status: 'flag', cutover: data.useSupabaseForData === true }
  } catch (error) {
    return { status: 'error', error }
  }
}

export type Unsubscribe = () => void

// ── ATL-1445 stage 3, P5: the WRITE plane rides on this one listener ────────────────────────────
//
// The write flag (`writesMaster`) lives on the same organization document, so the same listener
// observes it (stage-3 §4: "read both flags from the one Firestore listener"). Its rules live in
// `global/Write/writeCutoverWatch.ts`; this watcher only hands it each snapshot and runs what it
// decides. Without a `writePlane` the watcher is exactly ATL-888's. With one, a session whose writes
// stay Firestore-master is decided by the same read decision object, so the read behaviour is the
// same: `global/Write/__tests__/writeCutoverDifferential.test.js` runs master's watcher (a frozen,
// hash-checked copy) beside this one and compares their decisions, logs, timers and reloads.

/** One snapshot, both planes. `writes` is null unless `reads.status === 'flag'`. */
export interface OrgFlagsObservation {
  reads: ReadCutoverObservation
  writes: WritesMasterObservation | null
}

/** The restarts the write plane can ask for (stage-3 §4's rows; `writeCutoverWatch.ts`). */
export type OrgWritePlaneAction = 'write-cutover' | 'write-rollback' | 'read-rollback-after-v2-drain'

/** A restart the write plane asks for (`WriteCutoverDecision` in `writeCutoverWatch.ts`). */
export interface OrgWritePlaneTransition {
  action: OrgWritePlaneAction
  reload: true
  /** `capped`: ATL-888's at-most-once restart. `drain-v2-to-empty`: may wait for hours, so it is cancellable. */
  sequence: 'capped' | 'drain-v2-to-empty'
  /** Identifies the transition: the same key re-observed is the same transition, not a new one. */
  key: string
}

/**
 * The write plane the one listener drives. Generic over its own transition type, so the transition
 * `start` receives is exactly the one `decide` returned — no downcast between the two.
 */
export interface OrgWritePlaneWatch<T extends OrgWritePlaneTransition = OrgWritePlaneTransition> {
  observe: (snapshot: OrganizationDocSnapshot | null | undefined) => OrgFlagsObservation
  /** Must return the read decision object itself whenever the write plane does not move. */
  decide: (
    read: Omit<DecideReadCutoverReloadParams, 'observation'>,
    observation: OrgFlagsObservation
  ) => LiveReloadDecision | T
  /** Side effects of a server-backed observation (memory, alerts). Never throws into the watcher. */
  afterObservation: (observation: OrgFlagsObservation, decision: LiveReloadDecision | T) => void
  /** Starts a transition's sequence; returns its cancel. */
  start: (transition: T) => () => void
  /**
   * A drain-to-empty is already pending and another drain transition is decided (the same row
   * re-observed with a different target, or the other row): update what the pending sequence will do
   * at its end, without restarting it — no second grace, no repeated notice or page.
   */
  retarget: (transition: T) => void
  /** A drain-to-empty transition was cancelled before its restart. */
  cancelled: (key: string, why: string) => void
}

export const isWritePlaneTransition = <T extends OrgWritePlaneTransition>(
  decision: LiveReloadDecision | T
): decision is T => 'sequence' in decision

/** P4's provenance rule: only `metadata.fromCache === false` makes a write flag the truth. */
const isServerWriteObservation = (writes: WritesMasterObservation | null): boolean =>
  writes !== null && writes.kind === 'read' && writes.provenance === 'server'

export interface OrgReadCutoverWatch {
  /** Detach the listener and cancel any pending re-attach (and any write-plane transition it started). */
  unsubscribe: Unsubscribe
  /**
   * Re-run the decision against the most recent observation.
   *
   * MUST be called once `getInitialData` has recorded the session's data source: the flip can land
   * between the listener attaching and the branch running, and that observation would otherwise be
   * the only one this device ever sees.
   */
  reevaluate: () => void
  /**
   * P5: re-decide the WRITE plane alone against the most recent observation — once the seam's write
   * pin has landed (the write plane is deferred until then), or when the outbox v2 drainer asks for
   * a re-check after a `master-changed` answer. It starts a write transition or cancels a drain the
   * flag no longer calls for, and does nothing else: never the read reload, its logs, the flag
   * divergence report or the repeat suppression. A no-op without a `writePlane`, and for any
   * observation the read plane could not use (cached, absent, an error).
   */
  reevaluateWritePlane: () => void
}

export interface WatchOrganizationReadCutoverParams<
  T extends OrgWritePlaneTransition = OrgWritePlaneTransition,
> {
  organizationId: string | null | undefined
  /** Reads what this session is actually serving. See `sessionReadsSupabase`. */
  readSessionReadsSupabase: () => boolean | null
  /** `getDeviceSupabaseOverride()`, read lazily so a mid-session device toggle is respected. */
  readDeviceOverride: () => boolean
  /** `localDevSupabaseReads()`, read lazily for symmetry. */
  readLocalDev: () => boolean
  /**
   * Subscribes to the organization document. Injected so this is testable without a Firestore and
   * so the caller owns the SDK-shape details.
   */
  subscribe: (
    organizationId: string,
    onNext: (snapshot: OrganizationDocSnapshot | null) => void,
    onError: (error: unknown) => void
  ) => Unsubscribe
  /** Invoked at most once, when the app must restart onto a different data source. */
  onReload: (action: 'rollback' | 'cutover') => void
  /**
   * The LIVE `shouldUseSupabase()`. In Supabase read mode it resolves the flag from the Supabase
   * copy of the org record, whereas this watch reads the Firestore copy — so comparing them detects
   * the two halves of the dual-write having drifted apart. Optional; omit to disable the check.
   */
  readLiveResolvedSupabase?: () => boolean
  /** Reports a dual-write divergence. Diagnostic only — never acted on. */
  onFlagDivergence?: (detail: {
    firestoreCutover: boolean
    firestoreResolvesTo: boolean
    liveResolvesTo: boolean
    sessionReadsSupabase: boolean | null
  }) => void
  /** Schedules the re-attach after a listener error. Injected for tests. */
  scheduleTimeout?: (handler: () => void, ms: number) => unknown
  cancelTimeout?: (handle: unknown) => void
  logger?: Pick<Console, 'warn' | 'error' | 'log'>
  /** ATL-1445 P5: the write plane (`createWritePlaneWatch`). Omit for ATL-888's read-only watch. */
  writePlane?: OrgWritePlaneWatch<T>
}

/**
 * A Firestore `onSnapshot` error listener is TERMINAL — the SDK documents that no further callbacks
 * occur after `onError`. Without a re-attach, one transient `permission-denied` at startup (the
 * listener attaches before auth is known-good) would silently disarm the kill switch for the entire
 * shift. Backoff is capped so a permanently-denied listener does not spin.
 */
const REATTACH_DELAYS_MS = [1000, 5000, 15000, 60000]

/**
 * Watch the organization's read-cutover flag for the life of the session and fire `onReload` when
 * the resolved data source changes.
 *
 * `onReload` fires at most once — the decision rule already cannot loop, but this is the one code
 * path in the app that restarts it.
 *
 * Never throws: failing to arm a kill switch must not be what takes a till down.
 */
export const watchOrganizationReadCutover = <T extends OrgWritePlaneTransition = OrgWritePlaneTransition>({
  organizationId,
  readSessionReadsSupabase,
  readDeviceOverride,
  readLocalDev,
  subscribe,
  onReload,
  readLiveResolvedSupabase,
  onFlagDivergence,
  scheduleTimeout = setTimeout,
  // The handle is `unknown` so a test can schedule with any token it likes. Narrowed here, at the
  // only place that knows the handle really did come from the platform `setTimeout`.
  cancelTimeout = (handle: unknown) =>
    clearTimeout(handle as Parameters<typeof clearTimeout>[0]),
  logger = console,
  writePlane,
}: WatchOrganizationReadCutoverParams<T>): OrgReadCutoverWatch => {
  if (!organizationId) {
    logger.warn('[read-cutover] no organizationId; the live rollback watch is NOT armed')
    return { unsubscribe: () => {}, reevaluate: () => {}, reevaluateWritePlane: () => {} }
  }

  let reloaded = false
  let stopped = false
  let detach: Unsubscribe | null = null
  let reattachHandle: unknown = null
  let reattachAttempt = 0
  /** The most recent observation, so it can be re-decided once the session mode is known. */
  let lastObservation: OrgFlagsObservation | null = null
  /** Suppresses log spam when an unrelated org-doc edit re-reports an unchanged flag. */
  let lastAction: LiveReloadAction | OrgWritePlaneAction | null = null
  /** P5: a drain-to-empty transition in progress (it may wait for hours, so it stays cancellable). */
  let pendingDrain: { key: string; action: OrgWritePlaneAction; cancel: () => void } | null = null
  /** P5: the cancel of a capped write transition this watch started. */
  let cappedWriteCancel: (() => void) | null = null

  const cancelPendingDrain = (why: string): void => {
    if (!pendingDrain) return
    const { key, cancel } = pendingDrain
    pendingDrain = null
    try {
      cancel()
    } catch (error) {
      logger.error('[write-cutover] cancelling a pending drain-to-empty failed', { organizationId, error })
    }
    logger.warn('📊 [write-cutover] the pending drain-to-empty restart was CANCELLED', { organizationId, key, why })
    try {
      writePlane?.cancelled(key, why)
    } catch {
      // Telemetry only.
    }
  }

  const startWriteTransition = (transition: T): void => {
    if (!writePlane) return
    if (transition.sequence === 'capped') {
      cancelPendingDrain(`superseded by ${transition.key}`)
      logger.error(
        '📊 [write-cutover] LIVE WRITE-MASTER CHANGE — this session writes in another mode than a fresh ' +
          'start would; restarting so it cannot write against the wrong master',
        { organizationId, key: transition.key }
      )
      try {
        cappedWriteCancel = writePlane.start(transition)
        // Latched only once the restart is actually armed: a sequence that failed to start must not
        // also disarm the READ kill switch (it latches on the same flag) for the rest of the session.
        reloaded = true
      } catch (error) {
        logger.error('📊 [write-cutover] the restart sequence could not start; the next observation retries', {
          organizationId,
          error,
        })
      }
      return
    }
    if (pendingDrain) {
      // Both rows end the same way (drain to empty, then the restart), and the restart lands where a
      // fresh start decides; only the memory the drain leaves behind can differ. Retarget it in place.
      pendingDrain.action = transition.action
      try {
        writePlane.retarget(transition)
      } catch (error) {
        logger.error('[write-cutover] retargeting the pending drain-to-empty failed', { organizationId, error })
      }
      return
    }
    logger.error(
      '📊 [write-cutover] LIVE ROLLBACK OF A SUPABASE-MASTER SESSION — draining outbox v2 to empty, then ' +
        'restarting',
      { organizationId, key: transition.key }
    )
    try {
      pendingDrain = { key: transition.key, action: transition.action, cancel: writePlane.start(transition) }
    } catch (error) {
      logger.error('📊 [write-cutover] the drain-to-empty could not start', { organizationId, error })
    }
  }

  /**
   * P5: is a pending drain-to-empty still called for, given a decision that is NOT a drain
   * transition? Only evidence that could have STARTED it may stop it: a write rollback was started
   * by a SERVER-confirmed write flag, so only a server-confirmed write flag cancels it (a cached or
   * metadata-less snapshot, or a garbled value, is no evidence — P4's rule); a read rollback was
   * started by a readable read flag, so a readable read flag cancels it.
   */
  const maybeCancelPendingDrain = (
    observation: OrgFlagsObservation,
    decision: LiveReloadDecision | T
  ): void => {
    if (!pendingDrain || isWritePlaneTransition(decision)) return
    if (observation.reads.status !== 'flag') return
    if (pendingDrain.action === 'write-rollback' && !isServerWriteObservation(observation.writes)) return
    cancelPendingDrain(`the flag no longer calls for it (${decision.action})`)
  }

  const decideBothPlanes = (
    observation: OrgFlagsObservation,
    sessionReadsSupabase: boolean | null = readSessionReadsSupabase(),
    deviceOverride: boolean = readDeviceOverride(),
    localDev: boolean = readLocalDev()
  ): LiveReloadDecision | T => {
    const readDecision = (): LiveReloadDecision =>
      decideReadCutoverReload({
        sessionReadsSupabase,
        deviceOverride,
        localDev,
        observation: observation.reads,
      })
    if (!writePlane) return readDecision()
    try {
      return writePlane.decide({ sessionReadsSupabase, deviceOverride, localDev }, observation)
    } catch (error) {
      // A broken write plane must not disarm the READ kill switch: decide the read plane alone.
      logger.error('📊 [write-cutover] the write-plane decision failed; deciding reads alone', {
        organizationId,
        error,
      })
      return readDecision()
    }
  }

  const afterObservation = (
    observation: OrgFlagsObservation,
    decision: LiveReloadDecision | T
  ): void => {
    if (!writePlane || observation.reads.status !== 'flag') return
    try {
      writePlane.afterObservation(observation, decision)
    } catch (error) {
      logger.error('[write-cutover] the write-plane bookkeeping failed', { organizationId, error })
    }
  }

  /**
   * P5: re-decide the WRITE plane alone, against the most recent observation (the pin has landed,
   * or the drainer asks for a re-check). It acts only on a write transition, or on a pending drain
   * the flag no longer calls for. It never runs the read plane's reload, its logs, its divergence
   * report or its repeat suppression — so a session whose writes stay Firestore-master sees exactly
   * ATL-888's read behaviour whether or not this is ever called.
   */
  const decideWritePlaneOnly = (observation: OrgFlagsObservation): void => {
    if (!writePlane || observation.reads.status !== 'flag') return
    const decision = decideBothPlanes(observation)
    afterObservation(observation, decision)
    if (isWritePlaneTransition(decision)) {
      if (reloaded) return
      startWriteTransition(decision)
      return
    }
    maybeCancelPendingDrain(observation, decision)
  }

  const decide = (observation: OrgFlagsObservation): void => {
    lastObservation = observation
    const sessionReadsSupabase = readSessionReadsSupabase()
    const deviceOverride = readDeviceOverride()
    const localDev = readLocalDev()
    const decision = decideBothPlanes(observation, sessionReadsSupabase, deviceOverride, localDev)

    // ── Dual-write divergence detection ──────────────────────────────────────────────────────
    // This watch resolves the flag from FIRESTORE. In Supabase read mode `organizationAtom` is
    // refilled from the SUPABASE copy, so the ~55 per-call `shouldUseSupabase()` sites resolve it
    // from a different store than this does. If the two halves of the dual-write have drifted, the
    // running session and the kill switch disagree about the flag — and every check that reads
    // either one alone would report success.
    //
    // Diagnostic only. Acting on it would mean guessing which half is authoritative, and the whole
    // reason this class of bug survives is people acting on the half they happened to read.
    const readObservation = observation.reads
    if (readObservation.status === 'flag' && readLiveResolvedSupabase) {
      const firestoreSaysSupabase = localDev || deviceOverride || readObservation.cutover
      let live: boolean | null = null
      try {
        live = readLiveResolvedSupabase()
      } catch {
        live = null
      }
      if (live !== null && live !== firestoreSaysSupabase) {
        onFlagDivergence?.({
          firestoreCutover: readObservation.cutover,
          firestoreResolvesTo: firestoreSaysSupabase,
          liveResolvesTo: live,
          sessionReadsSupabase,
        })
      }
    }

    afterObservation(observation, decision)

    const repeated = decision.action === lastAction
    lastAction = decision.action

    if (isWritePlaneTransition(decision)) {
      if (reloaded) return
      startWriteTransition(decision)
      return
    }

    maybeCancelPendingDrain(observation, decision)

    if (decision.reload) {
      if (reloaded) return
      cancelPendingDrain(`superseded by the read ${decision.action}`)
      reloaded = true
      const rollingBack = decision.action === 'rollback'
      logger.error(
        rollingBack
          ? '📊 [read-cutover] LIVE ROLLBACK — the organization has been switched back to ' +
              'Firestore; reloading so this session stops reading Supabase'
          : '📊 [read-cutover] LIVE CUTOVER — the organization has been switched to Supabase; ' +
              'reloading so this session does not serve two stores at once',
        { organizationId }
      )
      onReload(decision.action as 'rollback' | 'cutover')
      return
    }

    switch (decision.action) {
      case 'unresolved':
        // Loud, and repeated on purpose while the session serves Supabase: this is the state in
        // which the kill switch cannot see the flag, and nobody finds that out from silence.
        if (sessionReadsSupabase === true) {
          logger.error(
            '📊 [read-cutover] CANNOT READ THE ORGANIZATION FLAG while this session reads ' +
              'Supabase — the live rollback is BLIND until a re-attach succeeds',
            { organizationId, observation: readObservation }
          )
        } else if (!repeated) {
          logger.warn(
            '[read-cutover] organization flag not readable (or not fresh); no action taken',
            { organizationId, sessionReadsSupabase, observation: readObservation }
          )
        }
        return
      case 'deferred':
        if (!repeated) {
          logger.log(
            '[read-cutover] flag observed before the session chose a data source; retained and ' +
              'will be re-decided once it does',
            { organizationId }
          )
        }
        return
      case 'pinned':
        if (repeated) return
        logger.warn(
          '📊 [read-cutover] the organization flag is OFF but this device reads Supabase via the ' +
            'device/local-dev override — the org kill switch does NOT control this session. ' +
            'Turn the device toggle off.',
          { organizationId }
        )
        return
      default:
        return
    }
  }

  const observeBothPlanes = (snapshot: OrganizationDocSnapshot | null): OrgFlagsObservation => {
    if (!writePlane) return { reads: observeOrganizationSnapshot(snapshot), writes: null }
    try {
      return writePlane.observe(snapshot)
    } catch (error) {
      // As in `decideBothPlanes`: the read kill switch survives a broken write plane.
      logger.error('📊 [write-cutover] the write-plane observation failed; observing reads alone', {
        organizationId,
        error,
      })
      return { reads: observeOrganizationSnapshot(snapshot), writes: null }
    }
  }

  const attach = (): void => {
    if (stopped) return
    try {
      detach = subscribe(
        organizationId,
        (snapshot) => {
          reattachAttempt = 0 // a successful emission clears the backoff
          decide(observeBothPlanes(snapshot))
        },
        (error) => {
          // Terminal for this listener — see REATTACH_DELAYS_MS.
          detach = null
          decide({ reads: { status: 'error', error }, writes: null })
          scheduleReattach()
        }
      )
    } catch (error) {
      detach = null
      logger.error(
        '📊 [read-cutover] could not attach the live rollback watch; retrying',
        { organizationId, error }
      )
      scheduleReattach()
    }
  }

  const scheduleReattach = (): void => {
    if (stopped) return
    const delay = REATTACH_DELAYS_MS[Math.min(reattachAttempt, REATTACH_DELAYS_MS.length - 1)]
    reattachAttempt += 1
    reattachHandle = scheduleTimeout(() => {
      reattachHandle = null
      attach()
    }, delay)
  }

  attach()

  return {
    unsubscribe: () => {
      stopped = true
      if (reattachHandle !== null) {
        cancelTimeout(reattachHandle)
        reattachHandle = null
      }
      try {
        detach?.()
      } catch {
        // Detaching a listener must never throw out of a React cleanup.
      }
      detach = null
      // P5: a write-plane restart this watch started must not outlive it (unmount, org switch).
      // ATL-888's read restart is cancelled by its caller, which owns it.
      if (pendingDrain) cancelPendingDrain('the watch was detached')
      if (cappedWriteCancel) {
        try {
          cappedWriteCancel()
        } catch {
          // Cancelling must never throw out of a React cleanup.
        }
        cappedWriteCancel = null
      }
    },
    reevaluate: () => {
      if (lastObservation) decide(lastObservation)
    },
    reevaluateWritePlane: () => {
      if (lastObservation) decideWritePlaneOnly(lastObservation)
    },
  }
}

/**
 * The grace period between deciding to restart and restarting.
 *
 * Not zero, because the active cart in `NewSale` is component state and a reload drops it: a
 * cashier mid-sale would lose the basket with no explanation. Not long, because a rollback is an
 * incident response. Five seconds turns "hours" into "seconds" while leaving time to read the
 * toast.
 *
 * After the grace period the restart also waits for in-flight backend writes to drain
 * (`LIVE_ROLLBACK_DRAIN_CAP_MS`) — see ATL-889 and `awaitDrain` below.
 */
export const LIVE_ROLLBACK_GRACE_MS = 5000

/**
 * ATL-889 — hard ceiling on waiting for in-flight writes before restarting.
 *
 * The dual-write commits to Firestore first and awaits Supabase second, and the ATL-782 outbox only
 * enqueues on an observed REJECTION. A runtime teardown produces none, so restarting mid-write
 * loses the Supabase mirror silently — the drift class the campaign has spent weeks eliminating.
 *
 * **Capped, and the cap is the point: a kill switch a hung request can veto is not a kill switch.**
 * When the cap expires the restart happens anyway and says so, because "we waited and it drained"
 * and "we gave up and restarted regardless" must never be spelled the same way. Three seconds
 * comfortably covers a normal write and is short next to the 5s grace period the cashier is already
 * being shown.
 */
export const LIVE_ROLLBACK_DRAIN_CAP_MS = 3000

export interface PerformLiveRollbackParams {
  /** Shows the user-facing warning. */
  notify: () => void
  /** Restarts the app. */
  reload: () => void
  graceMs?: number
  scheduleTimeout?: (handler: () => void, ms: number) => unknown
  /**
   * ATL-889 — waits for in-flight backend writes, capped. Injected so this module does not depend
   * on the API layer. Omit to skip the wait entirely (the pre-ATL-889 behaviour).
   */
  awaitDrain?: () => Promise<{ drained: boolean; remaining: number; waitedMs: number }>
  logger?: Pick<Console, 'error' | 'warn' | 'log'>
  /**
   * The log tag. ATL-1452's bundle lockout restarts through this same drain-then-restart sequence
   * (not a copy of it), and its logs must not read as a read-cutover incident.
   */
  label?: string
  /**
   * What an expired drain MEANS to whoever reads the log. The default describes a read-cutover
   * rollback; the bundle lockout passes its own, so on-call never starts a cutover incident over a
   * till that merely restarted into its update.
   */
  drainExpiredMessage?: string
  /** The log line when the reload itself throws (same reason as `drainExpiredMessage`). */
  reloadFailedMessage?: string
}

/**
 * Warn the operator, wait out the grace period, let in-flight writes finish, then restart.
 *
 * The restart happens even if the drain times out, and even if the drain check itself throws — a
 * kill switch that a hung request or a broken helper can veto is not a kill switch.
 *
 * @returns a cancel function. The restart is abortable so that unmounting, or a flag flipped back
 *          inside the grace window, does not leave a restart armed against a session that no longer
 *          needs one. Cancellation is re-checked AFTER the drain, since that is another await.
 */
export const performLiveRollback = ({
  notify,
  reload,
  graceMs = LIVE_ROLLBACK_GRACE_MS,
  scheduleTimeout = setTimeout,
  awaitDrain,
  logger = console,
  label = 'read-cutover',
  drainExpiredMessage = 'restarting with writes STILL IN FLIGHT — the drain cap expired. ' +
    'Their Supabase mirrors may be lost without a rejection (ATL-889); reconcile this ' +
    'org after the incident.',
  reloadFailedMessage = 'THE RELOAD FAILED — this device is STILL SERVING THE OLD DATA SOURCE ' +
    'and must be restarted by hand',
}: PerformLiveRollbackParams): (() => void) => {
  try {
    notify()
  } catch (error) {
    // A failed toast must never be the reason a kill switch does not fire.
    logger.error(`[${label}] restart notice failed to display`, { error })
  }

  let cancelled = false

  const restart = (): void => {
    if (cancelled) return
    try {
      reload()
    } catch (error) {
      logger.error(`📊 [${label}] ${reloadFailedMessage}`, { error })
    }
  }

  scheduleTimeout(() => {
    if (cancelled) return
    if (!awaitDrain) {
      restart()
      return
    }

    void (async () => {
      try {
        const result = await awaitDrain()
        if (!result.drained) {
          // Disclosed, never silent: this is the case where the restart MAY have dropped a Supabase
          // mirror, and it is the only warning anyone will get that it happened.
          logger.warn(
            `📊 [${label}] ${drainExpiredMessage}`,
            { remaining: result.remaining, waitedMs: result.waitedMs }
          )
        } else if (result.waitedMs > 0) {
          logger.log(`[${label}] in-flight writes drained before restart`, {
            waitedMs: result.waitedMs,
          })
        }
      } catch (error) {
        // The drain is a safety improvement, not a precondition. If it cannot run, restart anyway.
        logger.error(
          `[${label}] could not check in-flight writes; restarting regardless`,
          { error }
        )
      }
      restart()
    })()
  }, graceMs)

  return () => {
    cancelled = true
  }
}
